import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Check, CircleAlert, Clock3, ExternalLink, Info, Package, Upload, X } from 'lucide-react';
import type { wave } from '@round/api';
import AccountAvatar from '@/creator/components/AccountAvatar';
import StatusPill from '@/creator/components/StatusPill';
import { formatHandle } from '@/utility/formatHandle';
import { PLATFORM_LABEL } from '@/utility/campaignDisplay';
import { reportPostUrlValidationFailure, validatePostUrl } from '@/utility/postUrlValidation';
import {
    CONTRACT_PILL_STATUS,
    canAcceptContract,
    canReacceptContract,
    CONTRACT_STATUS_LABEL,
    PRICE_TYPE_LABEL,
    formatContractProgress,
    formatContractTerms,
    formatContractWindow,
    formatWindowLength,
    getContractPostsLeft,
    getContractSubmitBlock,
    isBundle,
} from '@/utility/contracts';
import type { PostCap } from '@/utility/contracts';

export type ContractResponse = {
    response: wave.InviteResponseChoice;
    reason?: string;
    creatorAccounts?: number[];
};

type Props = {
    contract: wave.CreatorContract;
    /** The contract's accounts as the invite carries them, with avatars and profile links. */
    accounts: wave.InviteAccount[];
    /** The post cap this card shows; defaults to the contract's own. */
    postCap?: PostCap;
    platform: wave.InvitePlatform;
    nowMs: number;
    /** A declined offer the invite can still take back up. */
    canReconsider?: boolean;
    /** Answer this contract; resolves true once it's saved. Omitted where the creator can't act. */
    onRespond?: (contract: wave.CreatorContract, answer: ContractResponse) => Promise<boolean>;
    /** Submit a post against this contract; resolves to an error message, or null when it went in. */
    onSubmitPost?: (contract: wave.CreatorContract, postUrl: string) => Promise<string | null>;
    /** The page's own view of this contract's posts. */
    children?: ReactNode;
    /** Reasons a decline must pick from; without them the reason is optional free text. */
    declineReasons?: string[];
    /** When this offer pays out, shown where the invite's offers pay out differently. */
    payoutUnlocks?: string;
};

const AccountChip = ({ account }: { account: wave.InviteAccount }) => {
    const content = (
        <>
            <AccountAvatar src={account.avatar_url} alt={account.handle} />
            <span className="public-invited-account__identity">
                <b>{formatHandle(account.platform, account.handle)}</b>
                <small>{PLATFORM_LABEL[account.platform]}</small>
            </span>
        </>
    );
    if (!account.profile_url) {
        return <div className="public-invited-account">{content}</div>;
    }
    return (
        <a
            className="public-invited-account public-invited-account--link"
            href={account.profile_url}
            target="_blank"
            rel="noreferrer"
            aria-label={`Open ${account.handle} on ${PLATFORM_LABEL[account.platform]}`}
        >
            {content}
            <ExternalLink className="public-invited-account__external" size={13} aria-hidden="true" />
        </a>
    );
};

/**
 * One offer on an invite: its terms, accounts, window, progress and posts, with its own
 * answer and submit box, so every action and post says which offer it's for.
 */
const ContractCard = ({
    contract,
    accounts,
    postCap,
    platform,
    nowMs,
    canReconsider,
    onRespond,
    onSubmitPost,
    children,
    declineReasons,
    payoutUnlocks,
}: Props) => {
    const [postUrl, setPostUrl] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResponding, setIsResponding] = useState(false);
    const [isChoosingAccounts, setIsChoosingAccounts] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);
    const [declineReason, setDeclineReason] = useState('');
    const [isConfirmingWithdraw, setIsConfirmingWithdraw] = useState(false);
    const [selectedAccounts, setSelectedAccounts] = useState<number[]>(() => accounts.map((account) => account.id));

    const windowLength = formatWindowLength(contract.duration_hours);
    const windowNote = formatContractWindow(contract, nowMs, postCap);
    const isWindowClosed = contract.status === 'accepted' && contract.submission_availability === 'reservation_expired';
    const isUnansweredOnClosedCampaign = contract.status === 'proposed' && contract.submission_availability === 'ended';
    const postsLeft = getContractPostsLeft(contract, postCap);
    const submitBlock = getContractSubmitBlock(contract, postCap);
    const hasLivePost = contract.post_counts.approved + contract.post_counts.pending > 0;
    const canAnswer = Boolean(onRespond) && contract.status === 'proposed';
    const canAccept = canAcceptContract(contract);
    const canReaccept = Boolean(onRespond) && canReacceptContract(contract);
    const canWithdraw = Boolean(onRespond) && contract.status === 'accepted' && !hasLivePost;
    const canTakeBack = Boolean(onRespond) && contract.status === 'declined' && Boolean(canReconsider);
    const canSubmit = Boolean(onSubmitPost) && submitBlock === null;
    const className = [
        'contract-card',
        `contract-card--${contract.status}`,
        contract.status === 'accepted' && isWindowClosed ? 'is-closed' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const respond = async (answer: ContractResponse): Promise<boolean> => {
        if (!onRespond) {
            return false;
        }
        setIsResponding(true);
        try {
            return await onRespond(contract, answer);
        } finally {
            setIsResponding(false);
        }
    };

    const accept = async () => {
        if (accounts.length > 1 && !isChoosingAccounts) {
            setIsChoosingAccounts(true);
            setIsDeclining(false);
            return;
        }
        if (await respond({ response: 'accepted', creatorAccounts: selectedAccounts })) {
            setIsChoosingAccounts(false);
        }
    };

    const isDeclineReasonMissing = Boolean(declineReasons) && !declineReason;

    const decline = async () => {
        if (isDeclineReasonMissing) {
            return;
        }
        if (await respond({ response: 'declined', reason: declineReason.trim() })) {
            setIsDeclining(false);
            setIsConfirmingWithdraw(false);
            setDeclineReason('');
        }
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const value = postUrl.trim();
        if (!value) {
            setSubmitError('Add the link to your live post.');
            return;
        }
        const validation = validatePostUrl(platform, value);
        if (!validation.isValid) {
            reportPostUrlValidationFailure(value, validation.message);
            setSubmitError(validation.message);
            return;
        }
        setIsSubmitting(true);
        try {
            const error = await onSubmitPost?.(contract, validation.url);
            setSubmitError(error ?? '');
            if (!error) {
                setPostUrl('');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const accountPicker = (
        <fieldset className="public-invited-account-list">
            <legend>Which accounts will take part?</legend>
            {accounts.map((account) => (
                <label className="public-invited-account" key={account.id}>
                    <input
                        type="checkbox"
                        checked={selectedAccounts.includes(account.id)}
                        onChange={(event) =>
                            setSelectedAccounts((current) =>
                                event.target.checked
                                    ? [...current, account.id]
                                    : current.filter((id) => id !== account.id)
                            )
                        }
                    />
                    <AccountAvatar src={account.avatar_url} alt={account.handle} />
                    <span className="public-invited-account__identity">
                        <b>{formatHandle(account.platform, account.handle)}</b>
                        <small>{PLATFORM_LABEL[account.platform]}</small>
                    </span>
                </label>
            ))}
            {selectedAccounts.length === 0 ? (
                <small className="submission-status__reason" role="alert">
                    Choose at least one account.
                </small>
            ) : null}
        </fieldset>
    );

    const declineReasonField = declineReasons ? (
        <label className="field">
            <span>Reason</span>
            <div>
                <select
                    aria-label="Decline reason"
                    value={declineReason}
                    onChange={(event) => setDeclineReason(event.target.value)}
                >
                    <option value="">Select a reason</option>
                    {declineReasons.map((reason) => (
                        <option key={reason}>{reason}</option>
                    ))}
                </select>
            </div>
        </label>
    ) : (
        <label className="field field--textarea">
            <span>Anything you want to share? (optional)</span>
            <div>
                <textarea
                    value={declineReason}
                    onChange={(event) => setDeclineReason(event.target.value)}
                    placeholder="e.g. timing doesn't work for me"
                />
            </div>
        </label>
    );

    const acceptButton = (label: string) => (
        <button
            className="button button--primary"
            type="button"
            disabled={isResponding || (isChoosingAccounts && selectedAccounts.length === 0)}
            onClick={accept}
        >
            <Check size={14} /> <span>{isChoosingAccounts ? 'Confirm accounts' : label}</span>
        </button>
    );

    return (
        <article className={className}>
            <div className="contract-card__header">
                <span className="contract-card__type">
                    <Package size={13} /> {PRICE_TYPE_LABEL[contract.price_type]}
                </span>
                <StatusPill status={isUnansweredOnClosedCampaign ? 'closed' : CONTRACT_PILL_STATUS[contract.status]}>
                    {isWindowClosed
                        ? 'Window closed'
                        : isUnansweredOnClosedCampaign
                          ? 'Closed'
                          : CONTRACT_STATUS_LABEL[contract.status]}
                </StatusPill>
            </div>
            <strong className="contract-card__terms">{formatContractTerms(contract, postCap)}</strong>
            <div className="contract-card__meta">
                {contract.status === 'proposed' && windowLength ? (
                    <span>{windowLength} to post once you accept</span>
                ) : null}
                {windowNote ? (
                    <span className={isWindowClosed ? 'is-closed' : ''}>
                        <Clock3 size={12} /> {windowNote}
                    </span>
                ) : null}
                {contract.status !== 'proposed' && contract.status !== 'declined' && contract.status !== 'cancelled' ? (
                    <span>{formatContractProgress(contract, postCap)}</span>
                ) : null}
                {payoutUnlocks ? <span>Payout unlocks: {payoutUnlocks.toLowerCase()}</span> : null}
            </div>
            {accounts.length > 0 && !isChoosingAccounts ? (
                <div className="contract-card__section">
                    <span className="contract-card__label">Accounts</span>
                    <div className="public-invited-account-list contract-card__accounts">
                        {accounts.map((account) => (
                            <AccountChip account={account} key={account.id} />
                        ))}
                    </div>
                </div>
            ) : null}
            {contract.status === 'cancelled' && contract.resolution_reason ? (
                <small className="contract-card__note">{contract.resolution_reason}</small>
            ) : null}
            {isUnansweredOnClosedCampaign ? (
                <small className="contract-card__note">
                    This campaign has closed, so this offer can&apos;t be accepted.
                </small>
            ) : null}
            {submitBlock === 'post_limit' ? (
                <small className="contract-card__note">
                    You&apos;ve posted as much as this campaign allows for now. Check back later.
                </small>
            ) : null}
            {isWindowClosed && isBundle(contract) && hasLivePost ? (
                <small className="contract-card__note">
                    {postsLeft !== null && postsLeft > 0
                        ? 'The posting window has closed. This bundle is unfinished, and may earn a reduced payout.'
                        : 'The posting window has closed. This bundle pays for its approved posts when it settles.'}
                </small>
            ) : null}
            {contract.status === 'accepted' && submitBlock === 'all_posted' ? (
                <small className="contract-card__note">
                    {contract.post_counts.pending > 0
                        ? 'Every post is in. We’ll review the rest soon.'
                        : 'Every post is in.'}
                </small>
            ) : null}
            {submitBlock === 'full' ||
            (contract.status === 'proposed' && contract.submission_availability === 'full') ? (
                <p className="cpm-rate-note contract-card__warning">
                    <Info className="cpm-rate-note__icon" size={12} aria-hidden="true" />
                    <span>
                        Every spot on this campaign has been claimed, so this offer can&apos;t take more posts right
                        now. Keep an eye on it, since this could change.
                    </span>
                </p>
            ) : null}

            {children ? (
                <div className="contract-card__section">
                    <span className="contract-card__label">Posts</span>
                    {children}
                    {contract.post_counts.pending > 0 ? (
                        <small className="contract-card__note contract-card__note--review">
                            Posts are usually reviewed within a couple of days.
                        </small>
                    ) : null}
                </div>
            ) : null}

            {canAnswer || canTakeBack ? (
                <div className="contract-card__respond">
                    {canTakeBack ? (
                        <small className="contract-card__note">
                            You&apos;ve let the brand know you&apos;re sitting this one out. Changed your mind?
                        </small>
                    ) : null}
                    {isChoosingAccounts ? accountPicker : null}
                    <div className="contract-card__actions">
                        {canTakeBack || canAccept ? acceptButton(canTakeBack ? 'I’m in after all' : 'I’m in') : null}
                        {isChoosingAccounts ? (
                            <button
                                className="button button--secondary"
                                type="button"
                                onClick={() => setIsChoosingAccounts(false)}
                            >
                                Cancel
                            </button>
                        ) : canAnswer ? (
                            <button
                                className="button button--secondary"
                                type="button"
                                disabled={isResponding}
                                onClick={() => setIsDeclining((current) => !current)}
                            >
                                <X size={14} /> <span>{canAccept ? 'Not this time' : 'I’m not interested'}</span>
                            </button>
                        ) : null}
                    </div>
                    {isDeclining ? (
                        <>
                            {declineReasonField}
                            <div className="contract-card__actions">
                                <button
                                    className="button button--secondary"
                                    type="button"
                                    disabled={isResponding || isDeclineReasonMissing}
                                    onClick={decline}
                                >
                                    Confirm decline
                                </button>
                            </div>
                        </>
                    ) : null}
                </div>
            ) : null}

            {canReaccept ? (
                <div className="contract-card__respond">
                    <small className="contract-card__note">
                        Your reservation expired. Reserve your spot again before submitting; availability is first come,
                        first served.
                    </small>
                    <div className="contract-card__actions">
                        <button
                            className="button button--primary"
                            type="button"
                            disabled={isResponding}
                            onClick={() => void respond({ response: 'accepted' })}
                        >
                            <Check size={14} /> <span>{isResponding ? 'Reserving…' : 'Reserve my spot again'}</span>
                        </button>
                    </div>
                </div>
            ) : null}

            {canSubmit ? (
                <form className="contract-card__submit" onSubmit={handleSubmit} noValidate>
                    <label className={submitError ? 'field field--error' : 'field'}>
                        <span>
                            {postsLeft === null
                                ? 'Submit a post'
                                : `Submit a post · ${postsLeft} ${postsLeft === 1 ? 'post' : 'posts'} left`}
                        </span>
                        <div>
                            <Upload size={16} />
                            <input
                                aria-label={`Post link for ${formatContractTerms(contract)}`}
                                aria-invalid={Boolean(submitError)}
                                inputMode="url"
                                autoCapitalize="none"
                                autoCorrect="off"
                                value={postUrl}
                                onChange={(event) => {
                                    setPostUrl(event.target.value);
                                    setSubmitError('');
                                }}
                                placeholder={`Paste your ${PLATFORM_LABEL[platform]} post URL`}
                            />
                        </div>
                        {submitError ? (
                            <small role="alert">
                                <CircleAlert size={13} /> <span>{submitError}</span>
                            </small>
                        ) : null}
                    </label>
                    <button className="button button--primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting…' : 'Submit for review'}
                    </button>
                </form>
            ) : null}

            {canWithdraw ? (
                isConfirmingWithdraw ? (
                    <div className="contract-card__respond">
                        <small className="contract-card__note">
                            You&apos;ll give up your spot for this offer. You can accept again later while it&apos;s
                            still open.
                        </small>
                        {declineReasons ? declineReasonField : null}
                        <div className="contract-card__actions">
                            <button
                                className="button button--danger"
                                type="button"
                                disabled={isResponding || isDeclineReasonMissing}
                                onClick={decline}
                            >
                                {isResponding ? 'Declining…' : 'Decline offer'}
                            </button>
                            <button
                                className="button button--secondary"
                                type="button"
                                disabled={isResponding}
                                onClick={() => setIsConfirmingWithdraw(false)}
                            >
                                Keep my spot
                            </button>
                        </div>
                    </div>
                ) : (
                    <button
                        className="text-button contract-card__withdraw"
                        type="button"
                        disabled={isResponding}
                        onClick={() => setIsConfirmingWithdraw(true)}
                    >
                        Changed your mind?
                    </button>
                )
            ) : null}
        </article>
    );
};

export default ContractCard;
